import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { MergedAttribute } from "../../services/entity-definition.service";
import { enumOptions } from "../../services/entity-definition.service";
import { type MediaFile, type FileKeyMap, toMediaFile, fromMediaFile, fileToResizedDataUrl } from "../../utils/media";
import { toInputValue, fromInputValue } from "../../utils/datetime";
import "../clam-relation-autocomplete/clam-relation-autocomplete";
import "../clam-media-input/clam-media-input";

function detectAccept(key: string): string {
	const k = key.toLowerCase();
	if (/(video|mp4)/.test(k)) return "video/*";
	if (/(image|img|logo|hero|photo|avatar|thumbnail|banner|foto)/.test(k)) return "image/*";
	return "*/*";
}

@customElement("clam-object-input")
export class ClamObjectInput extends LitElement {
	static styles = css`
		:host { display: block; font-family: var(--clam-font); }
		.object-fields { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--clam-space-3, 12px); }
		.object-fields .full { grid-column: 1 / -1; }
		@media (min-width: 640px) { .object-fields { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); } }

		.sub-field { display: flex; flex-direction: column; gap: 4px; }
		.sub-field label { font-size: var(--clam-text-xs, 11px); font-weight: 700; color: var(--clam-text-muted, #6d6e71); text-transform: uppercase; letter-spacing: 0.04em; }
		.sub-field input, .sub-field textarea, .sub-field select {
			width: 100%; padding: 8px 10px; border: 1px solid var(--clam-border, #ddd); border-radius: var(--clam-radius-sm, 5px);
			font-size: var(--clam-text-sm, 13px); box-sizing: border-box; font-family: var(--clam-font); background: var(--clam-surface, #fff);
		}
		.sub-field input:focus, .sub-field textarea:focus, .sub-field select:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); box-shadow: 0 0 0 2px rgba(107,187,36,0.15); }

		.sub-field--group {
			grid-column: 1 / -1;
			border: 1px solid var(--clam-border, #e2e8f0); border-left: 3px solid var(--clam-light-green, #6bbb24);
			border-radius: var(--clam-radius-md, 8px); background: var(--clam-surface-soft, #fafbfc);
			padding: var(--clam-space-3, 12px);
		}
		.sub-field__group-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--clam-space-3, 12px); }
		.sub-field__group-title { font-size: var(--clam-text-md, 15px); font-weight: 700; color: var(--clam-darkgray, #424244); }
		.sub-field__count { font-size: 11px; background: var(--clam-almost-white, #eef0f3); color: var(--clam-text-muted, #666); padding: 1px 9px; border-radius: 9999px; font-weight: 600; }

		.array-items { display: flex; flex-direction: column; gap: var(--clam-space-3, 12px); }
		.array-item {
			border: 1px solid var(--clam-border, #e0e0e0); border-radius: var(--clam-radius-md, 8px);
			background: var(--clam-surface, #fff); padding: var(--clam-space-3, 12px); position: relative;
		}
		.array-item.open { border-color: var(--clam-light-green, #6bbb24); box-shadow: 0 0 0 1px rgba(107,187,36,0.15); }
		.array-item-header { display: flex; justify-content: space-between; align-items: center; cursor: pointer; min-height: 44px; user-select: none; -webkit-tap-highlight-color: transparent; }
		.array-item.open > .array-item-header { margin-bottom: var(--clam-space-3, 12px); padding-bottom: var(--clam-space-2, 8px); border-bottom: 1px dashed var(--clam-border, #e8e8e8); }
		.array-item-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; font-size: var(--clam-text-sm, 13px); color: var(--clam-text, #333); min-width: 0; }
		.array-item-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.array-item-index { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 9999px; background: var(--clam-light-green, #6bbb24); color: #fff; font-size: 11px; font-weight: 700; flex-shrink: 0; }
		.array-item-chevron { transition: transform .15s ease; color: var(--clam-text-muted, #9ca3af); flex-shrink: 0; }
		.array-item-chevron.open { transform: rotate(180deg); }
		.empty-hint { font-size: 12px; color: var(--clam-text-muted, #9ca3af); padding: 4px 0; margin: 0; }
		.btn-remove { background: none; border: 1px solid transparent; color: var(--clam-danger, #f44336); cursor: pointer; font-size: 16px; padding: 4px 8px; min-width: 32px; min-height: 32px; border-radius: var(--clam-radius-sm, 6px); line-height: 1; }
		.btn-remove:hover { background: rgba(244,67,54,0.1); border-color: rgba(244,67,54,0.3); }
		.btn-add {
			display: flex; align-items: center; justify-content: center; gap: 6px;
			border: 2px dashed var(--clam-border, #ccc); border-radius: var(--clam-radius-md, 8px); padding: var(--clam-space-3, 12px);
			cursor: pointer; color: var(--clam-text-muted, #666); font-size: var(--clam-text-sm, 13px); background: none; width: 100%; margin-top: var(--clam-space-2, 8px);
		}
		.btn-add:hover { border-color: var(--clam-light-green, #6bbb24); color: var(--clam-light-green, #6bbb24); background: rgba(107,187,36,0.04); }
	`;

	@property({ attribute: false }) attr!: MergedAttribute;
	@property({ attribute: false }) model: Record<string, unknown> = {};
	@property() path = "";

	updated() {
		if (!this.model) this.model = {};
	}

	private get isArray(): boolean {
		return this.attr.effectiveInputType === "objectArray" || this.attr.morphedVariableType === "objectArray";
	}

	private get subAttrs(): MergedAttribute[] {
		return this.attr.attributeAttributes || [];
	}

	private _expanded = new Set<number>();

	private toggleItem(i: number): void {
		if (this._expanded.has(i)) this._expanded.delete(i);
		else this._expanded.add(i);
		this.requestUpdate();
	}

	private defaultFor(attr: MergedAttribute): unknown {
		if (attr.morphedVariableType === "boolean") return false;
		if (attr.morphedVariableType === "number") return 0;
		if (attr.morphedVariableType === "stringArray" || attr.morphedVariableType === "objectArray") return [];
		if (attr.morphedAttributeType === "nested" && attr.morphedVariableType === "object") return {};
		return "";
	}

	private get _isFileArray(): boolean {
		return !!this.attr.fileKeys && this.isArray;
	}

	private _kindFromMime(mime: string): string {
		const m = (mime || "").toLowerCase();
		if (m.startsWith("image/")) return "image";
		if (m.startsWith("video/")) return "video";
		if (m === "application/pdf") return "pdf";
		if (m.startsWith("audio/")) return "other";
		if (m.includes("word") || m.includes("excel") || m.includes("powerpoint") || m.includes("opendocument") || m.includes("text/")) return "document";
		return "other";
	}

	private _itemKind(itemValue: Record<string, unknown>): string {
		return String(itemValue.kind ?? itemValue.type ?? "").toLowerCase();
	}

	private _itemHasFile(itemValue: Record<string, unknown>, fk: FileKeyMap): boolean {
		return !!(itemValue[fk.url] || itemValue._base64);
	}

	private _triggerArrayFileInput(): void {
		const input = this.renderRoot.querySelector<HTMLInputElement>("#array-file-input");
		input?.click();
	}

	private async _onArrayFilesSelected(fileList: FileList | null): Promise<void> {
		if (!fileList || !this.attr.fileKeys) return;
		const fk = this.attr.fileKeys;
		const arr = Array.isArray(this.model[this.attr.attributeKey]) ? [...(this.model[this.attr.attributeKey] as Record<string, unknown>[])] : [];
		const files = Array.from(fileList);
		for (const file of files) {
			const res = await fileToResizedDataUrl(file);
			const base64 = res.dataUrl;
			const mf: MediaFile = { _base64: base64, filename: file.name, size: Math.round(base64.length * 0.75), mimeType: res.resized ? (file.type === "image/png" ? "image/png" : "image/jpeg") : file.type };
			const item: Record<string, unknown> = { ...fromMediaFile(mf, fk) };
			// defaults para los hijos no mapeados (metadata)
			const fileKeySet = new Set(Object.values(fk));
			for (const sub of this.subAttrs) {
				if (!fileKeySet.has(sub.attributeKey)) item[sub.attributeKey] = this.defaultFor(sub);
			}
			// auto-fill kind desde el mimeType (si el atributo tiene kind/type)
			const kindAttr = this.subAttrs.find((s) => s.attributeKey === "kind" || s.attributeKey === "type");
			if (kindAttr) item[kindAttr.attributeKey] = this._kindFromMime(file.type);
			arr.push(item);
			this._expanded.add(arr.length - 1);
		}
		this.model = { ...this.model, [this.attr.attributeKey]: arr };
		this.dispatchEvent(new CustomEvent("change", { detail: this.model }));
		this.requestUpdate();
	}

	private _fileToBase64(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	private addItem() {
		if (!this.isArray) return;
		const arr = Array.isArray(this.model[this.attr.attributeKey]) ? [...this.model[this.attr.attributeKey] as unknown[]] : [];
		const newItem: Record<string, unknown> = {};
		for (const sub of this.subAttrs) {
			newItem[sub.attributeKey] = this.defaultFor(sub);
		}
		arr.push(newItem);
		this._expanded.add(arr.length - 1);
		this.model = { ...this.model, [this.attr.attributeKey]: arr };
		this.dispatchEvent(new CustomEvent("change", { detail: this.model }));
		this.requestUpdate();
	}

	private removeItem(index: number) {
		const arr = [...(this.model[this.attr.attributeKey] as unknown[])];
		arr.splice(index, 1);
		const next = new Set<number>();
		this._expanded.forEach((idx) => { if (idx < index) next.add(idx); else if (idx > index) next.add(idx - 1); });
		this._expanded = next;
		this.model = { ...this.model, [this.attr.attributeKey]: arr };
		this.dispatchEvent(new CustomEvent("change", { detail: this.model }));
		this.requestUpdate();
	}

	private updateField(key: string, value: unknown) {
		this.model = { ...this.model, [key]: value };
		this.dispatchEvent(new CustomEvent("change", { detail: this.model }));
		this.requestUpdate();
	}

	private updateArrayItem(index: number, key: string, value: unknown) {
		const arr = [...(this.model[this.attr.attributeKey] as Record<string, unknown>[])];
		arr[index] = { ...arr[index], [key]: value };
		this.model = { ...this.model, [this.attr.attributeKey]: arr };
		this.dispatchEvent(new CustomEvent("change", { detail: this.model }));
		this.requestUpdate();
	}

	private renderItemLabel(item: Record<string, unknown>, index: number): string {
		if (this.attr.fileKeys) {
			const fnameKey = this.attr.fileKeys.filename;
			const fname = item[fnameKey];
			if (fname && typeof fname === "string") return fname;
		}
		const preferred = this.subAttrs.find((a) => ["name", "title", "label", "role", "year"].includes(a.attributeKey));
		const displayAttr = preferred || this.subAttrs.find((a) => a.morphedVariableType === "string") || this.subAttrs[0];
		const displayKey = displayAttr?.attributeKey;
		if (!displayKey) return `Item ${index + 1}`;
		const val = item[displayKey];
		return val !== undefined && val !== null && typeof val !== "object" ? String(val) : `Item ${index + 1}`;
	}

	render() {
		if (this.isArray) return this.renderArray();
		return this.renderObject();
	}

	private renderArray() {
		const items = (this.model[this.attr.attributeKey] as Record<string, unknown>[]) || [];
		const accept = this._isFileArray ? detectAccept(this.attr.attributeKey) : "";
		return html`
			<div class="array-items">
				${items.length === 0 ? html`<p class="empty-hint">${this._isFileArray ? "Sin archivos." : "Sin elementos."}</p>` : null}
				${items.map((item, i) => {
					const open = this._expanded.has(i);
					return html`
						<div class="array-item ${open ? "open" : ""}">
							<div class="array-item-header" @click=${() => this.toggleItem(i)}>
								<span class="array-item-title">
									<span class="array-item-index">${i + 1}</span>
									<span class="array-item-label">${this.renderItemLabel(item, i)}</span>
									<svg class="array-item-chevron ${open ? "open" : ""}" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
								</span>
								<button class="btn-remove" title="Eliminar" @click=${(e: Event) => { e.stopPropagation(); this.removeItem(i); }}>×</button>
							</div>
							${open ? this.renderItemBody(item, (next) => this.updateArrayItemWhole(i, next)) : null}
						</div>
					`;
				})}
			</div>
			${this._isFileArray
				? html`<input id="array-file-input" type="file" accept=${accept} multiple hidden @change=${(e: Event) => { const inp = e.target as HTMLInputElement; void this._onArrayFilesSelected(inp.files); inp.value = ""; }}>
					<button class="btn-add" @click=${() => this._triggerArrayFileInput()}>+ Subir archivo${this.attr.morphedVariableType === "objectArray" ? "s" : ""}</button>`
				: html`<button class="btn-add" @click=${() => this.addItem()}>+ Añadir</button>`}
		`;
	}

	private renderObject() {
		const objVal = (this.model[this.attr.attributeKey] as Record<string, unknown>) || {};
		if (this.subAttrs.length === 0 && !this.attr.fileKeys) {
			return html`<p style="color:#999;font-size:12px">Objeto sin sub-atributos definidos</p>`;
		}
		return this.renderItemBody(objVal, (next) => this.updateField(this.attr.attributeKey, next));
	}

	private renderItemBody(itemValue: Record<string, unknown>, onChange: (next: Record<string, unknown>) => void) {
		const fk = this.attr.fileKeys;
		if (fk) {
			const fileKeySet = new Set(Object.values(fk));
			const hasFile = this._itemHasFile(itemValue, fk);
			const kind = this._itemKind(itemValue);
			const others = this.subAttrs.filter((s) => {
				if (fileKeySet.has(s.attributeKey)) return false;
				// thumbnailUrl solo aplica si el archivo es imagen
				if (s.attributeKey === "thumbnailUrl") return kind === "image";
				return true;
			});
			const mf = toMediaFile(itemValue, fk);
			// cuando cambia el archivo, auto-recalcular kind desde el mimeType
			const onFileChange = (e: CustomEvent) => {
				const updated = { ...itemValue, ...fromMediaFile(e.detail as MediaFile, fk) };
				const kindAttr = this.subAttrs.find((s) => s.attributeKey === "kind" || s.attributeKey === "type");
				if (kindAttr) {
					const newMime = (e.detail as MediaFile)?.mimeType || "";
					updated[kindAttr.attributeKey] = this._kindFromMime(newMime);
				}
				onChange(updated);
			};
			return html`
				<div class="object-fields">
					<div class="sub-field full">
						<label>Archivo${this.attr.isRequired ? " *" : ""}</label>
						<clam-media-input
							.accept=${detectAccept(this.attr.attributeKey)}
							.multi=${false}
							.value=${mf}
							@change=${onFileChange}
						></clam-media-input>
					</div>
					${others.map((sub) => {
						const isKind = sub.attributeKey === "kind" || sub.attributeKey === "type";
						if (isKind && hasFile) {
							// kind lockeado: se asigna por el archivo, solo se libera quitando el archivo
							const opts = enumOptions(sub);
							const val = String(itemValue[sub.attributeKey] ?? "");
							return html`<div class="sub-field">
								<label>${sub.label ?? sub.attributeName}${sub.isRequired ? " *" : ""} <span style="font-weight:400;color:var(--clam-text-muted,#9ca3af);text-transform:none;letter-spacing:0">(auto)</span></label>
								<select disabled title="Se asigna automáticamente según el archivo. Quita el archivo para cambiarlo.">
									<option value=${val} selected>${val || "—"}</option>
									${opts.filter((o) => o !== val).map((o) => html`<option value=${o} ?selected=${val === o}>${o}</option>`)}
								</select>
							</div>`;
						}
						return this.renderSubField(sub, itemValue[sub.attributeKey], (val) => onChange({ ...itemValue, [sub.attributeKey]: val }));
					})}
				</div>
			`;
		}
		return html`
			<div class="object-fields">
				${this.subAttrs.map((sub) => this.renderSubField(sub, itemValue[sub.attributeKey], (val) => onChange({ ...itemValue, [sub.attributeKey]: val })))}
			</div>
		`;
	}

	private updateArrayItemWhole(index: number, value: Record<string, unknown>) {
		const arr = [...(this.model[this.attr.attributeKey] as Record<string, unknown>[])];
		arr[index] = value;
		this.model = { ...this.model, [this.attr.attributeKey]: arr };
		this.dispatchEvent(new CustomEvent("change", { detail: this.model }));
		this.requestUpdate();
	}

	private renderSubField(attr: MergedAttribute, value: unknown, onChange: (val: unknown) => void) {
		if (attr.effectiveInputType === "object" || attr.effectiveInputType === "objectArray") {
			const count = Array.isArray(value) ? value.length : 0;
			return html`
				<div class="sub-field sub-field--group full">
					<div class="sub-field__group-header">
						<span class="sub-field__group-title">${attr.label ?? attr.attributeName}${attr.isRequired ? " *" : ""}</span>
						${attr.effectiveInputType === "objectArray" ? html`<span class="sub-field__count">${count}</span>` : null}
					</div>
					<clam-object-input
						.attr=${attr}
						.model=${{ [attr.attributeKey]: value }}
						@change=${(e: CustomEvent) => onChange((e.detail as Record<string, unknown>)[attr.attributeKey])}
					></clam-object-input>
				</div>
			`;
		}
		const label = html`<label>${attr.label ?? attr.attributeName}${attr.isRequired ? " *" : ""}</label>`;
		const isFullWidth = ["text-area", "object", "objectArray", "file-upload"].includes(attr.effectiveInputType) ||
			attr.morphedVariableType === "stringArray";

		let input: unknown = html`<input type="text" />`;

		if (attr.effectiveInputType === "autocomplete" && attr.relatedWith) {
			input = html`<clam-relation-autocomplete
				.relatedEntity=${attr.relatedWith}
				.multi=${attr.morphedVariableType === "idArray"}
				.value=${value as string | string[] | null}
				@change=${(e: CustomEvent) => onChange(e.detail)}
			></clam-relation-autocomplete>`;
		} else if (attr.effectiveInputType === "file-upload") {
			const fk = attr.fileKeys;
			const isMulti = attr.morphedVariableType === "objectArray" || attr.morphedVariableType === "stringArray";
			const translated = fk
				? (isMulti
					? (Array.isArray(value) ? value.map((v) => toMediaFile(v, fk)) : [])
					: toMediaFile(value, fk))
				: (value as MediaFile | MediaFile[] | null);
			input = html`<clam-media-input
				.accept=${detectAccept(attr.attributeKey)}
				.multi=${isMulti}
				.maxSize=${10 * 1024 * 1024}
				.value=${translated ?? null}
				@change=${(e: CustomEvent) => {
					if (!fk) { onChange(e.detail); return; }
					const d = e.detail;
					onChange(isMulti ? (Array.isArray(d) ? d.map((m: MediaFile) => fromMediaFile(m, fk)) : []) : fromMediaFile(d as MediaFile, fk));
				}}
			></clam-media-input>`;
		} else if (attr.effectiveInputType === "select") {
			const opts = enumOptions(attr);
			input = html`<select @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value)}>
				<option value="">—</option>
				${opts.map((o) => html`<option value=${o} ?selected=${value === o}>${o}</option>`)}
			</select>`;
		} else if (attr.effectiveInputType === "switch" || attr.morphedVariableType === "boolean") {
			input = html`<select @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value === "true")}>
				<option value="false" ?selected=${!value}>No</option>
				<option value="true" ?selected=${value}>Sí</option>
			</select>`;
		} else if (attr.effectiveInputType === "number") {
			input = html`<input type="number" .value=${value ?? ""} @input=${(e: InputEvent) => onChange(Number((e.target as HTMLInputElement).value))} />`;
		} else if (attr.effectiveInputType === "text-area" || attr.morphedVariableType === "stringArray") {
			const val = Array.isArray(value) ? value.join("\n") : String(value || "");
			input = html`<textarea rows="2" .value=${val} @input=${(e: InputEvent) => {
				const raw = (e.target as HTMLTextAreaElement).value;
				onChange(attr.morphedVariableType === "stringArray" ? raw.split("\n").filter(Boolean) : raw);
			}}></textarea>`;
		} else if (attr.effectiveInputType === "datetime") {
			input = html`<input type="datetime-local" .value=${toInputValue(value, attr, "datetime")} @input=${(e: InputEvent) => onChange(fromInputValue((e.target as HTMLInputElement).value, attr, "datetime"))} />`;
		} else if (attr.effectiveInputType === "date") {
			input = html`<input type="date" .value=${toInputValue(value, attr, "date")} @input=${(e: InputEvent) => onChange(fromInputValue((e.target as HTMLInputElement).value, attr, "date"))} />`;
		} else if (attr.effectiveInputType === "time") {
			input = html`<input type="time" .value=${toInputValue(value, attr, "time")} @input=${(e: InputEvent) => onChange(fromInputValue((e.target as HTMLInputElement).value, attr, "time"))} />`;
		} else if (attr.effectiveInputType === "email") {
			input = html`<input type="email" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`;
		} else if (attr.effectiveInputType === "url") {
			input = html`<input type="url" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`;
		} else {
			input = html`<input type="text" .value=${value ?? ""} @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)} />`;
		}

		return html`<div class="sub-field ${isFullWidth ? "full" : ""}">${label}${input}</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"clam-object-input": ClamObjectInput;
	}
}
